<script lang="ts">
	import { onMount, tick } from 'svelte';
	import { handleStoreError } from '../../stores/GeneralStore';

	interface Props {
		id?: string | null;
		name?: string | null;
		value?: string | null;
		focusOnStart?: boolean;
		placeholder?: string;
		propertyName: string;
		maxLength?: number | null;
		disabled?: boolean;
		rightAlign?: boolean;
		onlyNumericValues?: boolean;
		callFunction?: ((object: any) => Promise<any>) | null;
		errors?: any;
		tabSwitcher?: boolean;
		wrappInOptionalParameter?: boolean;
		onBlur?: (e: { currentTarget: HTMLInputElement }) => void;
		onKeyup?: (e: KeyboardEvent & { currentTarget: HTMLInputElement }) => void;
		onFocus?: (e: FocusEvent) => void;
	}

	let {
		id = null,
		name = null,
		value = $bindable(''),
		focusOnStart = false,
		placeholder = '',
		propertyName,
		maxLength = 255,
		disabled = false,
		rightAlign = false,
		onlyNumericValues = false,
		callFunction = null,
		errors = {},
		tabSwitcher = false,
		wrappInOptionalParameter = false,
		onBlur = undefined,
		onKeyup = undefined,
		onFocus = undefined
	}: Props = $props();

	let element: HTMLInputElement | undefined = $state();

	onMount(async () => {
		if (element && focusOnStart) {
			await tick();
			element.focus();
			element.select();
		}
	});

	function handleBeforeInput(event: InputEvent) {
		const target = event.currentTarget as HTMLInputElement;
		//Build the next value
		const nextVal =
			target.value.substring(0, target.selectionStart || 0) +
			(event.data ?? '') +
			target.value.substring(target.selectionEnd || 0);

		if (maxLength && nextVal.length > maxLength) {
			event.preventDefault();
			return;
		}

		if (onlyNumericValues) {
			if (nextVal.match(/^-?[0-9]*$/g) == null) {
				event.preventDefault();
				return;
			}
		}
	}

	function handleOnInput(event: Event) {
		if (callFunction) return;
		const target = event.target as HTMLInputElement;
		value = target.value;
	}

	async function handleOnChange(event: { currentTarget: HTMLInputElement }) {
		const target = event.currentTarget as HTMLInputElement;
		if (callFunction) {
			try {
				const dynamicObject: any = {};

				//Check if we should wrapp the property in an optional
				if (wrappInOptionalParameter) {
					dynamicObject[propertyName] = { value: target.value };
				} else {
					dynamicObject[propertyName] = target.value;
				}

				await callFunction(dynamicObject);

				//Map it to a normal string
				value = target.value;
			} catch (error) {
				//Check if we have an error type
				handleStoreError('general.patch_failed', error);

				// Revert to last known good value
				if (element) {
					element.value = value || '';
				}
			}
		}
	}
</script>

<div class="flex flex-col">
	<input
		{id}
		data-testid={propertyName}
		autocomplete="off"
		{disabled}
		{name}
		bind:this={element}
		class:text-right={rightAlign}
		class:switcher={tabSwitcher}
		onbeforeinput={handleBeforeInput}
		oninput={handleOnInput}
		onblur={onBlur}
		onfocus={(e) => {
			e.currentTarget.select();
			onFocus?.(e);
		}}
		onchange={handleOnChange}
		onkeyup={onKeyup}
		{value}
		{placeholder}
	/>

	{#if errors[propertyName]}
		<p class="text-red dark:text-orange text-sm">{errors[propertyName]}</p>
	{/if}
</div>
